import { z } from 'zod';

const appSchema = {
  formSchema: () => {
    return z.object({
      username: z.string().min(1, 'Username is required'),
      password: z
        .string()
        .min(8, 'Password is required and must be 8 characters long'),
    });
  },
  addReferralForm: () => {
    return z
      .object({
        patientId: z.number().min(1, 'Patient ID is required'),
        referralDate: z.date({ required_error: 'Referral date is required' }),
        referredBy: z.number().min(1, 'Referring provider is required'),
        urgencyLevel: z.string({ required_error: 'Urgency level is required' }),
        referredTo: z.number().min(1, 'Recipient provider is required'),
        reasonForReferral: z.string().min(1, 'Reason for referral is required'),
        appointmentScheduled: z.boolean().optional(),
        patientInformed: z.boolean().optional(),
        appointmentDate: z.union([z.date(), z.null()]).optional(),
      })
      .refine(
        (data) => {
          if (data.appointmentScheduled && !data.appointmentDate) return false;
          return true;
        },
        {
          message: 'Appointment date is required when scheduled',
          path: ['appointmentDate'],
        },
      );
  },
  addPatientSchema: () => {
    return z.object({
      firstName: z.string().trim().min(1, 'First name is required'),
      lastName: z.string().trim().min(1, 'Last name is required'),
      dobDate: z.any().refine((val) => val !== null && val !== '', {
        message: 'Date of birth is required',
      }),
      patDemoGender: z.string().min(1, 'Sex is required'),
      patDemoHomePhone: z
        .string()
        .min(1, 'Home phone is required')
        .regex(
          /^\(\d{3}\) \d{3}-\d{4}$/,
          'Invalid phone format (000) 000-0000',
        ),
      patDemoZip: z
        .string()
        .trim()
        .min(5, 'Zip code must be at least 5 digits')
        .max(9, 'Zip code cannot exceed 9 digits')
        .regex(/^\d+$/, 'Zip code must contain only numbers'),
      patDemoEmail: z
        .string()
        .trim()
        .email('Invalid email address')
        .optional()
        .or(z.literal('')),
    });
  },
  referralProviderSchema: () => {
    return z
      .object({
        doctor_first_name: z
          .string()
          .min(3, 'Minimum 3 charachers is required')
          .trim()
          .optional()
          .or(z.literal('')),
        doctor_last_name: z
          .string()
          .min(3, 'Minimum 3 charachers is required')
          .trim()
          .optional()
          .or(z.literal('')),
        clinic_name: z
          .string()
          .min(3, 'Minimum 3 charachers is required')
          .trim()
          .optional()
          .or(z.literal('')),
        address_1: z.string().optional().or(z.literal('')),
        city: z.string().optional().or(z.literal('')),
        state: z.string().optional().or(z.literal('')),
        zip: z
          .string()
          .trim()
          .min(1, 'Zip code is required')
          .min(5, 'Zip code must be at least 5 digits')
          .max(10, 'Zip code cannot exceed 10 characters')
          .regex(
            /^\d{5}(-\d{4})?$/,
            'Invalid zip format (12345 or 12345-6789)',
          ),
        email: z
          .string()
          .email('Invalid email address')
          .optional()
          .or(z.literal('')),
        phone_l: z
          .string()
          .regex(
            /^\(\d{3}\) \d{3}-\d{4}$/,
            'Invalid phone format (000) 000-0000',
          )
          .optional()
          .or(z.literal('')),
        phone_m: z
          .string()
          .regex(
            /^\(\d{3}\) \d{3}-\d{4}$/,
            'Invalid phone format (000) 000-0000',
          )
          .optional()
          .or(z.literal('')),
        fax: z
          .string()
          .trim()
          .min(1, 'Fax is required')
          .regex(
            /^\(\d{3}\) \d{3}-\d{4}$/,
            'Invalid fax format (000) 000-0000',
          ),
        npi_no: z
          .string()
          .trim()
          .optional()
          .or(z.literal(''))
          .refine((val) => !val || /^\d{10}$/.test(val), {
            message: 'NPI must be exactly 10 digits',
          }),
      })
      .superRefine((data, ctx) => {
        if (
          !data.doctor_first_name &&
          !data.doctor_last_name &&
          !data.clinic_name
        ) {
          const message =
            'Please enter at least one: First Name, Last Name, or Clinic Name';
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['doctor_first_name'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['doctor_last_name'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['clinic_name'],
          });
        }
      });
  },

  referralFacilitySchema: (isRecipient) => {
    const base = {
      facility_name: z.string().min(1, 'Facility Name is required'),
      Specialty: z.string().optional().or(z.literal('')),
      address_1: z.string().optional().or(z.literal('')),
      city: z.string().optional().or(z.literal('')),
      state: z.string().optional().or(z.literal('')),
      zip: z
        .string()
        .trim()
        .min(1, 'Zip code is required')
        .min(5, 'Zip code must be at least 5 digits')
        .max(10, 'Zip code cannot exceed 10 characters')
        .regex(/^\d{5}(-\d{4})?$/, 'Invalid zip format (12345 or 12345-6789)'),
      email: z.string().optional().or(z.literal('')),
      phone_l: z.string().optional().or(z.literal('')),
      phone_m: z.string().optional().or(z.literal('')),
      fax: z
        .string()
        .trim()
        .min(1, 'Fax is required')
        .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Invalid fax format (000) 000-0000'),
    };

    if (isRecipient) {
      return z.object({
        ...base,
        Specialty: z.string().optional().or(z.literal('')),
        address_1: z.string().optional().or(z.literal('')),
        city: z.string().optional().or(z.literal('')),
        state: z.string().optional().or(z.literal('')),
        email: z
          .string()
          .email('Invalid email address')
          .optional()
          .or(z.literal('')),
        phone_l: z
          .string()
          .regex(
            /^\(\d{3}\) \d{3}-\d{4}$/,
            'Invalid phone format (000) 000-0000',
          )
          .optional()
          .or(z.literal('')),
      });
    }

    return z.object(base);
  },

  referralInsuranceSchema: () => {
    return z.object({
      insPayerName: z.string().min(1, 'Payer Name is required'),
      insuranceTypeId: z.string().min(1, 'Insurance Type is required'),
      insuranceAddress: z.string().optional().or(z.literal('')),
      insuranceCity: z.string().optional().or(z.literal('')),
      insuranceState: z.string().optional().or(z.literal('')),
      insuranceZip: z
        .string()
        .trim()
        .min(1, 'Zip code is required')
        .min(5, 'Zip code must be at least 5 digits')
        .max(10, 'Zip code cannot exceed 10 characters')
        .regex(/^\d{5}(-\d{4})?$/, 'Invalid zip format (12345 or 12345-6789)'),
      insuranceEmail: z
        .string()
        .email('Invalid email address')
        .optional()
        .or(z.literal('')),
      insuranceDeskPhone: z
        .string()
        .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Invalid phone format (000) 000-0000')
        .optional()
        .or(z.literal('')),
      insuranceCell: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine(
          (val) => !val || /^\(\d{3}\) \d{3}-\d{4}$/.test(val),
          'Invalid phone format (000) 000-0000',
        ),
      insuranceFax: z
        .string()
        .trim()
        .min(1, 'Fax is required')
        .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Invalid fax format (000) 000-0000'),
    });
  },
};

export default appSchema;
